import { Component, Input, OnChanges } from '@angular/core'
import { FolderOverviewActions, FolderScopeDetails } from '../store'

import { MatDialog } from '@angular/material/dialog'
import { Title } from '@angular/platform-browser'
import { UpdateComponentPayload } from '@app/shared/components/scope-elements/models/update-component-payload.interface'
import { Router } from '@angular/router'
import { CdnConfig } from '@app/core/model/cdn-config.model'
import { MenuOptions } from '@app/core/model/definitions/menu-options.interface'
import { ApplicationSetting } from '@app/core/model/enums/application-setting.enum'
import { Privilege } from '@app/core/model/enums/privilege.enum'
import { ScopeType } from '@app/core/model/enums/scope-type.enum'
import { FolderVersion } from '@app/core/model/folder-version.model'
import { ModalConfig } from '@app/core/model/modal-config.model'
import { ScopeVersion } from '@app/core/model/scope-version'
import { Preference } from '@app/core/model/user-preferences.interface'
import { AuthService } from '@app/core/service/auth.service'
import { LanguageService } from '@app/core/service/language.service'
import { ScopeElementsConfig, UpdateDeliverablePayload } from '@app/shared/components/scope-elements/models'
import { ScopeElementsService } from '@app/shared/components/scope-elements/services/scope-elements.service'
import { ScopeUiModalComponent } from '@app/shared/components/ui-components/scope-ui-modal/scope-ui-modal.component'
import { ScopeUiTableSelectMode } from '@app/shared/components/ui-components/scope-ui-table/scope-ui-table.component'
import { TableColumnPreferences } from '@app/shared/components/ui-components/scope-ui-table/table-column-preferences.type'
import { mergePreferencesWithTableConfig } from '@app/shared/utils/utils'
import { Store } from '@ngrx/store'
import { each } from 'lodash'
import { FolderScopesActions } from '../store/actions/folder-scopes.actions'
import { folderScopeDeliverablesColumnsBaseConfig } from '../store/const'
import { FolderOverviewService } from '../store/services'

@Component({
  selector: 'folder-overview-folder',
  templateUrl: './folder-overview-folder.component.html',
  styleUrls: ['./folder-overview-folder.component.scss'],
})
export class FolderOverviewFolderComponent implements OnChanges {
  @Input() folder: FolderVersion
  @Input() scopeDetailsMap: FolderScopeDetails
  @Input() tableConfig: any
  @Input() cdnConfig: CdnConfig

  searchString = ''
  menuOptions: MenuOptions[]
  scopeMenuOptions: MenuOptions[]
  createMenuOptions: MenuOptions[]

  get id(): number {
    return this.folderOverviewService.folderId
  }

  constructor(
    private store$: Store,
    private titleService: Title,
    private lang: LanguageService,
    private router: Router,
    private dialog: MatDialog,
    private folderOverviewService: FolderOverviewService,
    protected authService: AuthService,
    protected scopeElementsService: ScopeElementsService
  ) {
    this.titleService.setTitle('Folder Overview | Folder')
    this.setMenuOptions()
  }

  ngOnChanges() {
    if (this.folder) this.titleService.setTitle(`${this.folder.name} | Folder Overview | Folder`)
  }

  onTogglePreference(preference: Preference) {
    this.store$.dispatch(FolderScopesActions.updateColumnPreference({ preference }))
  }

  onUpdateScope(scope: ScopeVersion) {
    this.store$.dispatch(FolderScopesActions.updateScope({ scope }))
  }

  onUpdateDeliverable({ deliverable }: UpdateDeliverablePayload, scope: ScopeVersion) {
    this.store$.dispatch(FolderScopesActions.updateDeliverable({ scope, deliverable }))
  }

  onUpdateComponent({ component }: UpdateComponentPayload, scope: ScopeVersion) {
    this.store$.dispatch(FolderScopesActions.updateComponent({ scope, component }))
  }

  onSelectScope({ entity }) {
    this.router.navigate(['scopes', 'scope-overview', entity.identity.id])
  }

  onExpandScope({ entity }) {
    this.store$.dispatch(FolderScopesActions.getScopeDetails({ scope: entity }))
  }

  onExpandDeliverable({ scopeId, deliverableId }) {
    this.store$.dispatch(FolderScopesActions.getDeliverableDetails({ scopeId, deliverableId }))
  }

  deleteScope(scope: ScopeVersion) {
    const modalConfig: ModalConfig = {
      title: `Delete ${this.lang.get('scope')}`,
      body: `Are you sure you wish to delete '${scope.name}'?`,
      confirmText: `Delete`,
      cancelText: `Cancel`,
      cancelCallback: () => dialog.close(),
      confirmCallback: () => {
        dialog.close()
        this.store$.dispatch(FolderScopesActions.deleteScope({ id: scope.identity.id }))
      },
      limitBodyWidth: true,
      hasCancelButton: true,
    }

    const dialog = this.dialog.open(ScopeUiModalComponent, { data: modalConfig })
  }

  duplicateScope(scope: ScopeVersion) {
    this.router.navigate(['/duplicate-scope/' + scope.identity.id], {
      queryParams: { folderId: this.folder.identity.id },
    })
  }

  createScope(type: ScopeType) {
    switch (type) {
      case ScopeType.SCOPE_BY_WORK:
        this.router.navigate(['create-scope'], { queryParams: { folderId: this.folder.identity.id } })
        break
      case ScopeType.SCOPE_BY_ROLE:
        this.router.navigate(['create-scope-by-role'], { queryParams: { folderId: this.folder.identity.id } })
        break
      case ScopeType.SCOPE_BY_SCENARIO:
        this.router.navigate(['create-scope-by-scenario'], { queryParams: { folderId: this.folder.identity.id } })
        break
    }
  }

  convertToSingleScope(scope: ScopeVersion) {
    const modalConfig: ModalConfig = {
      title: `Convert to Single ${this.lang.get('scope')}`,
      body: `Are you sure you wish to move out '${scope.name}' from '${this.folder.name}'?`,
      confirmText: `Move`,
      cancelText: `Cancel`,
      cancelCallback: () => dialog.close(),
      confirmCallback: () => {
        dialog.close()
        this.store$.dispatch(FolderScopesActions.removeScope({ id: scope.identity.id }))
      },
      limitBodyWidth: true,
      hasCancelButton: true,
    }

    const dialog = this.dialog.open(ScopeUiModalComponent, { data: modalConfig })
  }

  deleteFolder() {
    const modalConfig: ModalConfig = {
      title: `Delete ${this.lang.get('sow')}`,
      body: `Are you sure you wish to delete '${this.folder.name}'?`,
      confirmText: `Delete`,
      cancelText: `Cancel`,
      cancelCallback: () => dialog.close(),
      confirmCallback: () => {
        dialog.close()
        this.store$.dispatch(FolderOverviewActions.deleteFolder({ id: this.folder.identity.id }))
      },
      limitBodyWidth: true,
      hasCancelButton: true,
    }

    const dialog = this.dialog.open(ScopeUiModalComponent, { data: modalConfig })
  }

  archiveFolder() {
    const modalConfig: ModalConfig = {
      title: `Archive ${this.lang.get('sow')}`,
      body: `Are you sure you wish to archive '${this.folder.name}'?`,
      confirmText: `Archive`,
      cancelText: `Cancel`,
      cancelCallback: () => dialog.close(),
      confirmCallback: () => {
        dialog.close()
        this.store$.dispatch(FolderOverviewActions.archiveFolder({ id: this.folder.identity.id }))
      },
      limitBodyWidth: true,
      hasCancelButton: true,
    }

    const dialog = this.dialog.open(ScopeUiModalComponent, { data: modalConfig })
  }

  unarchiveFolder() {
    const modalConfig: ModalConfig = {
      title: `Unarchive ${this.lang.get('sow')}`,
      body: `Are you sure you wish to unarchive '${this.folder.name}'?`,
      confirmText: `Unarchive`,
      cancelText: `Cancel`,
      cancelCallback: () => dialog.close(),
      confirmCallback: () => {
        dialog.close()
        this.store$.dispatch(FolderOverviewActions.unarchiveFolder({ id: this.folder.identity.id }))
      },
      limitBodyWidth: true,
      hasCancelButton: true,
    }

    const dialog = this.dialog.open(ScopeUiModalComponent, { data: modalConfig })
  }

  getConfig = (scopeVersion: ScopeVersion): ScopeElementsConfig => ({
    scopeVersion,
    userColumns: this.scopeElementsService.userColumns,
    deliverableColumns: this.getColumns(),
    currency: scopeVersion.currency,
    isEditable: false,
    isDefaultSection: true,
    showRiskIndicator: true,
    mainColumnClass: 'six--columns',
    componentMainColumnClass: 'eight--columns',
    roleMainColumnClass: 'nine--columns',
    hasFractionalQuantities: this.authService.loggedInUser.company.hasApplicationSetting(
      ApplicationSetting.COMPONENT__ENABLE_FRACTIONAL_QUANTITIES
    ),
  })

  getColumns = (): TableColumnPreferences =>
    mergePreferencesWithTableConfig(folderScopeDeliverablesColumnsBaseConfig, this.tableConfig?.columns)

  setMenuOptions() {
    this.scopeMenuOptions = [
      {
        callback: ({ entity }) => this.convertToSingleScope(entity),
        icon: () => 'exit_to_app',
        name: () => `Move out ${this.lang.get('sow|l')}`,
        hasPrivilege: () => Privilege.SCOPE__EDIT,
      },
      {
        isHidden: ({ entity }) => entity.isScopeByRole(),
        callback: ({ entity }) => this.duplicateScope(entity),
        icon: () => 'content_copy',
        name: () => `Duplicate ${this.lang.get('scope')}`,
        hasPrivilege: () => Privilege.SCOPE__CREATE,
      },
      {
        callback: ({ entity }) => this.deleteScope(entity),
        icon: () => 'delete',
        name: () => 'Delete',
        hasPrivilege: () => Privilege.SCOPE__CREATE,
      },
    ]

    this.menuOptions = [
      {
        callback: () => (this.folder?.isArchived() ? this.unarchiveFolder() : this.archiveFolder()),
        icon: () => (this.folder?.isArchived() ? 'unarchive' : 'archive'),
        name: () => (this.folder?.isArchived() ? 'Unarchive' : 'Archive'),
        hasPrivilege: () => Privilege.SOW__ARCHIVE,
      },
      {
        callback: () => this.deleteFolder(),
        icon: () => 'delete',
        name: () => 'Delete',
        hasPrivilege: () => Privilege.SOW__DELETE,
      },
    ]

    this.createMenuOptions = []

    if (this.authService.loggedInUser.hasPrivilege(Privilege.SCOPE__CREATE)) {
      this.createMenuOptions.push({
        name: () =>
          this.lang.get('scope') +
          (this.authService.loggedInUser.company.hasApplicationSetting(ApplicationSetting.SCOPE_BY_ROLE__ENABLE)
            ? ' by Work'
            : ''),
        icon: () => 'equalizer',
        callback: () => this.createScope(ScopeType.SCOPE_BY_WORK),
      })
    }

    if (
      this.authService.loggedInUser.hasPrivilege(Privilege.SCOPE__CREATE) &&
      this.authService.loggedInUser.company.hasApplicationSetting(ApplicationSetting.SCOPE_BY_ROLE__ENABLE)
    ) {
      this.createMenuOptions.push({
        name: () => this.lang.get('scope') + ' by Role',
        icon: () => 'table_chart',
        callback: () => this.createScope(ScopeType.SCOPE_BY_ROLE),
      })
    }

    if (
      this.authService.loggedInUser.hasPrivilege(Privilege.SCOPE__CREATE) &&
      this.authService.loggedInUser.company.hasApplicationSetting(ApplicationSetting.SCOPE_BY_SCENARIO) &&
      this.authService.loggedInUser.company.hasApplicationSetting(ApplicationSetting.SCOPE__ENABLE_SCENARIO_PRICING)
    ) {
      this.createMenuOptions.push({
        name: () => 'Scenario Pricing',
        icon: () => 'alt_route',
        callback: () => this.createScope(ScopeType.SCOPE_BY_SCENARIO),
      })
    }
  }

  hasPrivileges = (...privileges: Privilege[]) => each(privileges, (p) => this.authService.loggedInUser.hasPrivilege(p))
  hasSettings = (...settings: ApplicationSetting[]) =>
    each(settings, (s) => this.authService.loggedInUser.company.hasApplicationSetting(s))

  protected readonly scopeEditableFn = (scope: ScopeVersion) => scope.isDraftOrConfigDraft()
  protected readonly ScopeUiTableSelectMode = ScopeUiTableSelectMode
  protected readonly Privilege = Privilege
}
